
import React from 'react';
import { Check, ChevronRight, Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';

interface PricingCardProps {
  name: string;
  description: string;
  basePrice: number;
  price: number;
  period: string;
  features: string[];
  isPopular: boolean;
  ctaText: string;
  showFreeTrial: boolean;
  badge: string | null;
  billingPeriod: 'monthly' | 'yearly';
}

const PricingCard: React.FC<PricingCardProps> = ({
  name,
  description,
  basePrice,
  price,
  period,
  features,
  isPopular,
  ctaText,
  showFreeTrial,
  badge,
  billingPeriod
}) => {
  const calculateSavings = (monthlyPrice: number) => {
    const monthlyTotal = monthlyPrice * 12;
    const yearlyTotal = price * 12;
    return monthlyTotal - yearlyTotal;
  };

  return (
    <div 
      className={cn(
        "rounded-2xl overflow-hidden transition-all duration-300 h-full flex flex-col group hover:-translate-y-2",
        isPopular 
          ? 'shadow-xl border-2 border-yellow-500 transform' 
          : 'shadow-lg border border-gray-100 transform'
      )}
    >
      {badge && (
        <div className="bg-gradient-to-r from-yellow-500 to-yellow-300 text-center py-1.5">
          <span className="text-sm font-montserrat font-bold text-darkgray">{badge}</span>
        </div>
      )}
      
      <div className={`p-5 bg-white flex-grow flex flex-col ${!badge ? 'pt-7' : ''}`}>
        <div className="h-16 flex flex-col justify-center mb-2">
          <div className="flex items-center mb-1">
            <h3 className="text-2xl font-montserrat font-bold text-darkgray">{name}</h3>
          </div>
          <p className="text-darkgray/70 text-sm font-opensans">{description}</p>
        </div>
        
        <div className="h-20 flex flex-col justify-center mb-4">
          <div className="flex items-center">
            {typeof price === 'number' ? (
              <>
                {billingPeriod === 'yearly' && basePrice ? (
                  <div className="flex flex-col">
                    <div className="flex items-center">
                      <span className="text-3xl font-montserrat font-bold text-yellow-500">{price} kr</span>
                      <span className="text-darkgray/70 ml-2 text-sm font-opensans">{period}</span>
                    </div>
                    <div className="text-xs text-darkgray/70 mt-1 font-opensans">
                      <span className="line-through">{basePrice} kr</span> per användare/månad
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <span className="text-3xl font-montserrat font-bold text-yellow-500">{price} kr</span>
                    <span className="text-darkgray/70 ml-2 text-sm font-opensans">{period}</span>
                  </div>
                )}
              </>
            ) : (
              <span className="text-2xl font-montserrat font-bold text-yellow-500">{price}</span>
            )}
          </div>
          {billingPeriod === 'yearly' && basePrice && (
            <div className="mt-1 flex items-center">
              <Zap className="h-3.5 w-3.5 text-accent mr-1" />
              <span className="text-xs text-accent font-medium font-opensans">
                Spara {calculateSavings(basePrice)} kr per användare/år
              </span>
            </div>
          )}
        </div>
        
        <Separator className="bg-gray-200 my-2" />
        
        {showFreeTrial && (
          <div className="text-center mb-4 py-2 px-3 bg-yellow-300 border border-yellow-500 rounded-lg">
            <span className="text-sm font-medium text-darkgray font-opensans">14 dagars gratis provperiod</span>
          </div>
        )}
        
        <ul className="space-y-3 mb-6">
          {features.map((feature, i) => (
            <li key={i} className="flex items-start">
              <Check className="h-5 w-5 text-yellow-500 mr-2 mt-0.5 flex-shrink-0" />
              <span className="text-sm font-opensans">{feature}</span>
            </li>
          ))}
        </ul>
        
        <div className="mt-auto">
          <button 
            className={cn(
              "w-full py-3 rounded-xl transition-all duration-300 font-montserrat font-bold text-base group-hover:shadow-md flex items-center justify-center",
              isPopular 
                ? "bg-gradient-to-r from-yellow-500 to-yellow-300 hover:from-yellow-500 hover:to-yellow-500 text-darkgray" 
                : "bg-white border-2 border-darkgray text-darkgray hover:bg-darkgray hover:text-white"
            )}
          >
            {ctaText}
            <ChevronRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PricingCard;
